import { Modal } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAppDispatch } from 'common/hooks/redux'
import {
  procedureApi,
  useFetchStripeTerminalsQuery,
  usePayEncounterMutation,
} from 'features/Procedure/state/procedure.api'
import { ProcedureStoreTagTypes } from 'features/Procedure/state/procedure.type'

import type { CheckoutPreview } from '../checkout.utils'
import { ConfirmMarkBilledView } from './confirm-mark-billed-view'
import { classifyEncounterCheckoutStatus } from './encounter-checkout-pusher.utils'
import { buildPayEncounterBody } from './encounter-payment-dialog.utils'
import { FailedView } from './failed-view'
import { SelectTerminalView } from './select-terminal-view'
import { SuccessView } from './success-view'
import { useEncounterCheckoutPusher } from './use-encounter-checkout-pusher'
import { WaitingView } from './waiting-view'

import styles from './encounter-payment-dialog.module.scss'

type DialogStep = 'select' | 'confirm-mark-billed' | 'waiting' | 'success' | 'failed'

export type EncounterPaymentDialogResume = {
  amount: number
  stripeTerminalId: number
  message?: string | null
}

type Props = {
  open: boolean
  onClose: () => void
  siteId: string
  patientId: number
  encounterUid: string
  preview: CheckoutPreview
  resume?: EncounterPaymentDialogResume | null
}

const getErrorMessage = (error: unknown): string | undefined => {
  if (error && typeof error === 'object') {
    if ('data' in error) {
      const data = (error as { data?: unknown }).data
      if (data && typeof data === 'object' && 'message' in data) {
        const message = (data as { message?: unknown }).message
        if (typeof message === 'string' && message.length > 0) {
          return message
        }
      }
    }
    if ('message' in error) {
      const message = (error as { message?: unknown }).message
      if (typeof message === 'string' && message.length > 0) {
        return message
      }
    }
  }
  return undefined
}

export const EncounterPaymentDialog = ({
  open,
  onClose,
  siteId,
  patientId,
  encounterUid,
  preview,
  resume,
}: Props) => {
  const [step, setStep] = useState<DialogStep>(resume ? 'waiting' : 'select')
  const [selectedTerminalId, setSelectedTerminalId] = useState<number | null>(
    resume?.stripeTerminalId ?? null,
  )
  const [errorMessage, setErrorMessage] = useState<string | undefined>(resume?.message ?? undefined)
  const [editedAmount, setEditedAmount] = useState<number>(preview.totals.amountDue)
  const [submittedAmount, setSubmittedAmount] = useState<number | null>(resume?.amount ?? null)
  const [subscriptionGen, setSubscriptionGen] = useState(0)
  const paymentAttemptedRef = useRef(Boolean(resume))
  const dispatch = useAppDispatch()

  const {
    data: terminals = [],
    isFetching: isFetchingTerminals,
    isError: isTerminalsError,
  } = useFetchStripeTerminalsQuery({ siteId }, { skip: !open, refetchOnMountOrArgChange: true })

  const [payEncounter, { isLoading: isSending }] = usePayEncounterMutation()

  useEffect(() => {
    if (!open) {
      setStep('select')
      setSelectedTerminalId(null)
      setErrorMessage(undefined)
      setSubmittedAmount(null)
      if (paymentAttemptedRef.current) {
        dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.EncounterCheckout]))
        paymentAttemptedRef.current = false
      }
    } else {
      setEditedAmount(preview.totals.amountDue)
      if (resume) {
        setStep('waiting')
        setSelectedTerminalId(resume.stripeTerminalId)
        setSubmittedAmount(resume.amount)
        setErrorMessage(resume.message ?? undefined)
        paymentAttemptedRef.current = true
        setSubscriptionGen((prev) => prev + 1)
      }
    }
  }, [open, preview.totals.amountDue, resume, dispatch])

  useEffect(() => {
    if (selectedTerminalId === null && terminals.length > 0) {
      const firstOnline = terminals.find((terminal) => terminal.status !== 'offline')
      if (firstOnline) {
        setSelectedTerminalId(firstOnline.id)
      }
    }
  }, [selectedTerminalId, terminals])

  const selectedTerminal = useMemo(
    () => terminals.find((terminal) => terminal.id === selectedTerminalId) ?? null,
    [terminals, selectedTerminalId],
  )

  const amount = editedAmount
  const isCashBilledFlow = editedAmount === 0

  const submitPayment = useCallback(async () => {
    if (isCashBilledFlow) {
      paymentAttemptedRef.current = true
      try {
        await payEncounter({
          siteId,
          patientId,
          encounterUid,
          body: buildPayEncounterBody(preview, selectedTerminalId ?? 0, editedAmount),
        }).unwrap()
      } catch {
        // cash-billed flow has no error UI; parent refetch reflects BE state
      }
      onClose()
      return
    }

    paymentAttemptedRef.current = true
    setSubmittedAmount(editedAmount)
    setSubscriptionGen((prev) => prev + 1)
    setStep('waiting')
    setErrorMessage(undefined)

    try {
      await payEncounter({
        siteId,
        patientId,
        encounterUid,
        body: buildPayEncounterBody(preview, selectedTerminalId ?? 0, editedAmount),
      }).unwrap()
    } catch (error: unknown) {
      setErrorMessage(getErrorMessage(error))
      setStep('failed')
    }
  }, [
    editedAmount,
    encounterUid,
    isCashBilledFlow,
    onClose,
    patientId,
    payEncounter,
    preview,
    selectedTerminalId,
    siteId,
  ])

  const handleSend = useCallback(() => {
    if (isCashBilledFlow) {
      setStep('confirm-mark-billed')
      return
    }

    if (selectedTerminalId === null) return
    void submitPayment()
  }, [isCashBilledFlow, selectedTerminalId, submitPayment])

  const handleConfirmMarkBilled = useCallback(() => {
    void submitPayment()
  }, [submitPayment])

  const handleCancelMarkBilled = useCallback(() => {
    setStep('select')
  }, [])

  useEncounterCheckoutPusher({
    siteId,
    encounterUid,
    enabled: open && step === 'waiting',
    generation: subscriptionGen,
    onUpdate: useCallback(({ status, message }) => {
      const resolution = classifyEncounterCheckoutStatus(status)
      if (resolution === 'success') {
        setStep('success')
        return
      }
      if (resolution === 'failure') {
        if (message) {
          setErrorMessage(message)
        }
        setStep('failed')
        return
      }
      if (message) {
        setErrorMessage(message)
      }
    }, []),
  })

  const handleTryAgain = useCallback(() => {
    setStep('select')
    setErrorMessage(undefined)
    setSubmittedAmount(null)
  }, [])

  const handleDone = useCallback(() => {
    onClose()
  }, [onClose])

  const renderStep = () => {
    if (step === 'confirm-mark-billed') {
      return (
        <ConfirmMarkBilledView
          onCancel={handleCancelMarkBilled}
          onConfirm={handleConfirmMarkBilled}
          isSubmitting={isSending}
        />
      )
    }
    if (step === 'waiting') {
      return (
        <WaitingView
          amount={submittedAmount ?? amount}
          terminalLabel={selectedTerminal?.name ?? ''}
          message={errorMessage}
        />
      )
    }
    if (step === 'success') {
      return (
        <SuccessView
          amount={submittedAmount ?? amount}
          terminalLabel={selectedTerminal?.name ?? ''}
          onDone={handleDone}
        />
      )
    }
    if (step === 'failed') {
      return <FailedView message={errorMessage} onCancel={onClose} onTryAgain={handleTryAgain} />
    }

    return (
      <SelectTerminalView
        amount={amount}
        maxAmount={preview.totals.amountDue}
        onAmountChange={setEditedAmount}
        terminals={terminals}
        isLoading={isFetchingTerminals}
        isError={isTerminalsError}
        selectedTerminalId={selectedTerminalId}
        onSelectTerminal={setSelectedTerminalId}
        onCancel={onClose}
        onSend={handleSend}
        isSending={isSending}
      />
    )
  }

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      width={step === 'select' ? 436 : 340}
      centered
      classNames={{
        content: `${styles.modalContent} ${step === 'select' ? styles.modalContentWide : ''}`,
      }}>
      <div className={styles.body}>{renderStep()}</div>
    </Modal>
  )
}
